import { toFetchConferenceDetails } from "@/src/utils/callsUtils.js";
import { conferenceParticipantLabels } from "@/src/constants/callConstants.js";

export default {
  namespaced: true,

  state: {
    isWarmTransfer: false,
    conferenceSid: null,
    swappingCalls: false,
    transferringCall: false,
    transferTargetActive: false,
    callerOnHoldEventReceived: false,
    isMerged: false,
    merging: false,
    lastMergedTransferId: null,
    myCallSid: null,
    conferenceData: {
      transferNumber: null,
      transferName: null,
      transferPhoneNumber: null,
      transferCallID: null,
      parentCallID: null,
      childCallID: null,
      conferenceName: null,
      status: null,
      participants: [],
    },
  },

  mutations: {
    SET_IS_WARM_TRANSFER(state, value) {
      state.isWarmTransfer = value;
    },

    SET_CONFERENCE_SID(state, sid) {
      state.conferenceSid = sid;
    },

    SET_SWAPPING_CALLS(state, value) {
      state.swappingCalls = value;
    },

    SET_TRANSFERRING_CALL(state, value) {
      state.transferringCall = value;
    },

    SET_TRANSFER_TARGET_ACTIVE(state, value) {
      state.transferTargetActive = value;
    },

    SET_CALLER_ON_HOLD_EVENT_RECEIVED(state, value) {
      state.callerOnHoldEventReceived = value;
    },

    SET_IS_MERGED(state, value) {
      state.isMerged = value;
    },

    SET_MERGING(state, value) {
      state.merging = value;
    },

    SET_LAST_MERGED_TRANSFER_ID(state, id) {
      state.lastMergedTransferId = id;
    },

    SET_MY_CALL_SID(state, sid) {
      state.myCallSid = sid;
    },

    SET_CONFERENCE_DATA(state, data) {
      const {
        transfer_number,
        transfer_name,
        transfer_phone_number,
        transfer_call_id,
        parent_call_id,
        child_call_id,
        conference_name,
        conference_id,
        from,
        to,
        ...rest
      } = data;

      state.conferenceData = {
        ...state.conferenceData,
        transferNumber: transfer_number,
        transferName: transfer_name ?? state.conferenceData.transferName,
        transferPhoneNumber:
          transfer_phone_number ?? state.conferenceData.transferPhoneNumber,
        transferCallID: transfer_call_id,
        parentCallID: parent_call_id,
        childCallID: child_call_id,
        conferenceName: conference_name,
        ...rest,
      };

      if (conference_id) {
        state.conferenceSid = conference_id;
      }
    },

    SET_PARTICIPANTS(state, participants) {
      state.conferenceData.participants = participants;
    },

    ADD_PARTICIPANT(state, participant) {
      const exists = state.conferenceData.participants.some(
        (p) => p.phone_number === participant.phone_number,
      );

      state.conferenceData.participants.push(participant);
    },

    REMOVE_PARTICIPANT(state, identifier) {
      state.conferenceData.participants =
        state.conferenceData.participants.filter(
          (participant) =>
            participant.id !== identifier &&
            participant.callSid !== identifier &&
            participant.call_sid !== identifier,
        );
    },

    UPDATE_PARTICIPANT_STATUS(state, { participantId, status }) {
      const participant = state.conferenceData.participants.find(
        (p) => p.id === participantId,
      );
      if (participant) {
        participant.status = status;
      }
    },

    REMOVE_PARTICIPANTS_BY_LABEL(state, label) {
      state.conferenceData.participants =
        state.conferenceData.participants.filter((p) => p.label !== label);
    },

    UNHOLD_ALL_PARTICIPANTS(state) {
      state.conferenceData.participants =
        state.conferenceData.participants.map((p) => ({ ...p, hold: false }));
    },

    RESET_CONFERENCE(state) {
      state.isWarmTransfer = false;
      state.conferenceSid = null;
      state.swappingCalls = false;
      state.transferringCall = false;
      state.transferTargetActive = false;
      state.callerOnHoldEventReceived = false;
      state.isMerged = false;
      state.merging = false;
      state.lastMergedTransferId = null;
      state.myCallSid = null;
      state.conferenceData = {
        transferNumber: null,
        transferName: null,
        transferPhoneNumber: null,
        transferCallID: null,
        parentCallID: null,
        childCallID: null,
        status: null,
        conferenceName: null,
        participants: [],
      };
    },
  },

  actions: {
    startWarmTransfer({ commit }) {
      return new Promise((resolve) => {
        commit("SET_IS_WARM_TRANSFER", true);
        resolve(true);
      });
    },

    setSwappingCall({ commit }, value) {
      return new Promise((resolve) => {
        commit("SET_SWAPPING_CALLS", value);
        resolve(value);
      });
    },

    setTransferringCall({ commit }, value) {
      return new Promise((resolve) => {
        commit("SET_TRANSFERRING_CALL", value);
        resolve(value);
      });
    },

    setTransferTargetActive({ commit }, value) {
      return new Promise((resolve) => {
        commit("SET_TRANSFER_TARGET_ACTIVE", value);
        resolve(value);
      });
    },

    setCallerOnHoldEventReceived({ commit }, value) {
      return new Promise((resolve) => {
        commit("SET_CALLER_ON_HOLD_EVENT_RECEIVED", value);
        resolve(value);
      });
    },

    setMerged({ commit }, value) {
      return new Promise((resolve) => {
        commit("SET_IS_MERGED", value);
        resolve(value);
      });
    },

    setMerging({ commit }, value) {
      return new Promise((resolve) => {
        commit("SET_MERGING", value);
        resolve(value);
      });
    },

    markMerged({ commit }, transferId) {
      return new Promise((resolve) => {
        commit("SET_IS_MERGED", true);
        commit("SET_LAST_MERGED_TRANSFER_ID", transferId);
        resolve(transferId);
      });
    },

    setMyCallSid({ commit }, sid) {
      return new Promise((resolve) => {
        commit("SET_MY_CALL_SID", sid);
        resolve(sid);
      });
    },

    setConferenceData({ commit }, data) {
      return new Promise((resolve) => {
        commit("SET_CONFERENCE_DATA", data);
        commit("ADD_PARTICIPANT", {
          id: data.transfer_call_id,
          to: data.to,
          from: data.from,
          name: data.transfer_name,
          phone_number: data.transfer_phone_number,
          label: conferenceParticipantLabels.TRANSFER_TARGET,
          status: "ongoing",
        });
        resolve(data);
      });
    },

    setParticipants({ commit }, participants) {
      return new Promise((resolve) => {
        commit("SET_PARTICIPANTS", participants);
        resolve(participants);
      });
    },

    mergeParticipants({ commit, state }, incoming) {
      return new Promise((resolve) => {
        if (!Array.isArray(incoming)) {
          resolve([]);
          return;
        }
        const existing = state.conferenceData.participants;
        const merged = incoming.map((p) => {
          const match = existing.find(
            (e) =>
              (p.id && e.id && e.id === p.id) ||
              (p.phone_number &&
                e.phone_number &&
                e.phone_number === p.phone_number),
          );
          if (!match) return p;
          return {
            ...match,
            ...p,
            name: p.name || match.name,
            phone_number: p.phone_number || match.phone_number,
            label: p.label || match.label,
          };
        });
        commit("SET_PARTICIPANTS", merged);
        resolve(merged);
      });
    },

    addParticipant({ commit }, participant) {
      return new Promise((resolve) => {
        commit("ADD_PARTICIPANT", participant);
        resolve(participant);
      });
    },

    removeParticipant({ commit }, participantId) {
      return new Promise((resolve) => {
        commit("REMOVE_PARTICIPANT", participantId);
        resolve(participantId);
      });
    },

    updateParticipantStatus({ commit }, { participantId, status }) {
      return new Promise((resolve) => {
        commit("UPDATE_PARTICIPANT_STATUS", { participantId, status });
        resolve({ participantId, status });
      });
    },

    endConference({ commit }) {
      return new Promise((resolve) => {
        commit("RESET_CONFERENCE");
        resolve();
      });
    },

    cancelWarmTransfer({ commit }, label) {
      return new Promise((resolve) => {
        commit("REMOVE_PARTICIPANTS_BY_LABEL", label);
        commit("UNHOLD_ALL_PARTICIPANTS");
        commit("SET_IS_WARM_TRANSFER", false);
        commit("SET_TRANSFERRING_CALL", false);
        commit("SET_TRANSFER_TARGET_ACTIVE", false);
        commit("SET_CALLER_ON_HOLD_EVENT_RECEIVED", false);
        resolve();
      });
    },
  },

  getters: {
    isWarmTransfer: (state) => state.isWarmTransfer,
    conferenceSid: (state) => state.conferenceSid,
    conferenceData: (state) => state.conferenceData,
    participants: (state) => state.conferenceData.participants,
    swappingCalls: (state) => state.swappingCalls,
    transferringCall: (state) => state.transferringCall,
    transferTargetActive: (state) => state.transferTargetActive,
    callerOnHoldEventReceived: (state) => state.callerOnHoldEventReceived,
    isMerged: (state) => state.isMerged,
    merging: (state) => state.merging,
    lastMergedTransferId: (state) => state.lastMergedTransferId,
    myCallSid: (state) => state.myCallSid,
  },
};
